import { Rol } from "@copi/shared";
import { LogOut, User } from "lucide-react";
import { Link, Outlet, useNavigate } from "react-router-dom";

import { NotificacionesCampana } from "./NotificacionesCampana";
import { SidebarNavegacion } from "./SidebarNavegacion";
import { SidebarProcesoTesis } from "./SidebarProcesoTesis";
import { ThemeToggle } from "./ThemeToggle";
import { useContadoresDocencia } from "./useContadoresDocencia";

import { paths } from "@/app/paths";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/auth.context";
import { useSse } from "@/lib/realtime/use-sse";

/**
 * Shell principal de la aplicación: sidebar fijo + topbar + outlet.
 * Solo se renderiza en rutas protegidas.
 *
 * **ADR 016 (corrección D5):** el sidebar es polimórfico por rol —
 *  - Estudiante/Bachiller → `SidebarProcesoTesis` (stepper de 12 etapas
 *    de su propio proceso de tesis).
 *  - Resto de roles → `SidebarNavegacion` (bandejas administrativas).
 *
 * El topbar (notificaciones + perfil + logout) es invariante para todos
 * los roles (DRY).
 */
export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Logout explícito → /login SIN `state.from`. Si solo limpiáramos la sesión,
  // ProtectedRoute rebotaría a /login guardando `from` = la URL actual (p. ej.
  // /expedientes/:id); al entrar OTRO usuario lo mandaría al expediente del
  // anterior → 403. Navegar limpio resetea ese destino al dashboard.
  const handleLogout = async () => {
    await logout();
    navigate(paths.login(), { replace: true });
  };

  // ADR 018 — Conexión SSE viva mientras hay sesión. La conexión se cierra
  // automáticamente al desmontar el shell (logout o navegación a ruta
  // pública). Solo aquí — montar en cada feature crearía conexiones
  // paralelas (el backend cerraría las previas, pero igual desperdicia).
  useSse({ enabled: Boolean(user) });

  // Contadores de pendientes para los badges del sidebar (ADR 054). El hook
  // gatea sus queries por capacidad, así que es seguro llamarlo siempre.
  const { revisionesAsesor, revisionesJurado, nuevoAsesor, nuevoJurado } = useContadoresDocencia();

  if (!user) return null;

  const esEstudiante = user.rol === Rol.ESTUDIANTE || user.rol === Rol.BACHILLER;

  return (
    <div className="flex min-h-screen bg-background">
      {esEstudiante ? (
        <SidebarProcesoTesis />
      ) : (
        <SidebarNavegacion
          rol={user.rol}
          puedeAsesorar={user.puedeAsesorar}
          puedeSerJurado={user.puedeSerJurado}
          revisionesAsesor={revisionesAsesor}
          revisionesJurado={revisionesJurado}
          nuevoAsesor={nuevoAsesor}
          nuevoJurado={nuevoJurado}
        />
      )}
      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-border bg-card px-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {user.nombres} {user.apellidos}
            </h2>
            <p className="text-xs text-muted-foreground">{user.rol}</p>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <NotificacionesCampana />
            <Button asChild variant="ghost" size="icon" aria-label="Perfil">
              <Link to="/perfil">
                <User className="h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Salir
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
